import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ArrowRightIcon,
  ActivityIcon,
  DatabaseIcon,
  GlobeIcon,
  LoaderIcon,
  PlayIcon,
  RadarIcon,
  SparklesIcon,
  TrendingUpIcon
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { publicAnalysisApi } from '@/services/public-api'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AnimatedTooltip } from '@/components/ui/motion-tooltip'
import { BorderBeam } from '@/components/ui/border-beam'
import { Rating } from '@/components/ui/rating'

import BuildTAM from '@/components/shadcn-studio/blocks/hero-section-40/build-tam'
import OverlaySignals from '@/components/shadcn-studio/blocks/hero-section-40/overlay-signals'
import ExecuteSequences from '@/components/shadcn-studio/blocks/hero-section-40/execute-sequences'
import CaptureActivity from '@/components/shadcn-studio/blocks/hero-section-40/capture-activity'
import TrackPipeline from '@/components/shadcn-studio/blocks/hero-section-40/track-pipeline'
import AskProspectHunter from '@/components/shadcn-studio/blocks/hero-section-40/ask-prospect-hunter'
import { isValidWebsiteInput, normalizeWebsiteUrl } from '@/lib/website-url'

const avatars = [
  {
    image: 'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-45.png',
    fallback: 'AH',
    name: 'Ali Hussein',
    designation: 'Developer'
  },
  {
    image: 'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-59.png',
    fallback: 'SJ',
    name: 'Sahaj Jain',
    designation: 'Software Engineer'
  },
  {
    image: 'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-34.png',
    fallback: 'CD',
    name: 'Chánh Đại',
    designation: 'Design Engineer'
  },
  {
    image: 'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-58.png',
    fallback: 'J',
    name: 'Julian',
    designation: 'Senior Developer'
  }
]

const tabs = [
  {
    name: 'Build TAM',
    value: 'build-tam',
    icon: DatabaseIcon,
    content: <BuildTAM />
  },
  {
    name: 'Overlay Signals',
    value: 'overlay-signals',
    icon: RadarIcon,
    content: <OverlaySignals />
  },
  {
    name: 'Execute Sequences',
    value: 'execute-sequences',
    icon: PlayIcon,
    content: <ExecuteSequences />
  },
  {
    name: 'Capture Activity',
    value: 'capture-activity',
    icon: ActivityIcon,
    content: <CaptureActivity />
  },
  {
    name: 'Track Pipeline',
    value: 'track-pipeline',
    icon: TrendingUpIcon,
    content: <TrackPipeline />
  },
  {
    name: 'Ask ProspectHunter',
    value: 'ask-prospect-hunter',
    icon: SparklesIcon,
    content: <AskProspectHunter />
  }
]

const HeroSection = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(tabs[0]?.value || 'build-tam')
  const [websiteInput, setWebsiteInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab(currentTab => {
        const currentIndex = tabs.findIndex(tab => tab.value === currentTab)
        const nextIndex = (currentIndex + 1) % tabs.length

        return tabs[nextIndex].value
      })
    }, 7000)

    return () => clearInterval(interval)
  }, [activeTab])

  const handleGetStarted = async () => {
    setError(null)

    const normalizedUrl = normalizeWebsiteUrl(websiteInput)
    if (!normalizedUrl) {
      setError('Please enter a valid website URL')
      return
    }

    setIsSubmitting(true)

    try {
      const result = await publicAnalysisApi.startAnalysis(normalizedUrl)
      navigate(`/analyze/${result.id}`)
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number; data?: { detail?: string } } }
        if (axiosError.response?.status === 429) {
          setError(axiosError.response.data?.detail || 'Rate limit exceeded. Please try again later.')
        } else {
          setError(axiosError.response?.data?.detail || 'Failed to start analysis. Please try again.')
        }
      } else {
        setError('Failed to start analysis. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSubmitting) {
      e.preventDefault()
      handleGetStarted()
    }
  }

  return (
    <section className='relative flex flex-col overflow-hidden'>
      <div className='border-b px-4 sm:px-6 lg:px-8'>
        <div className='mx-auto flex max-w-7xl flex-col gap-6 border-x px-4 py-8 sm:px-6 sm:py-16 lg:px-8 lg:py-24'>
          <div className='flex flex-col items-center gap-4 text-center'>
            <Badge variant='outline' className='bg-muted relative gap-2.5 px-1.5 py-1'>
              <span className='bg-primary text-primary-foreground flex h-5.5 items-center rounded-full px-2 py-0.5'>
                Try it for free!
              </span>
              <span className='text-muted-foreground text-sm font-normal text-wrap'>Just Launched!</span>
              <BorderBeam colorFrom='var(--primary)' colorTo='var(--primary)' size={35} />
            </Badge>

            <h1 className='text-2xl font-semibold sm:text-3xl lg:text-5xl lg:leading-[1.29167]'>
              The first revenue
              <br />
              engine for your company
            </h1>

            <p className='text-muted-foreground max-w-3xl text-xl'>
              An AI-native alternative to outdated CRMs and disconnected sales software
            </p>

            <div className='flex w-full max-w-xl flex-col items-center gap-2'>
              <div className='relative flex w-full items-center'>
                <GlobeIcon className='text-muted-foreground absolute left-4 hidden size-5 sm:block' />
                <Input
                  id="hero-website-input"
                  type="text"
                  placeholder="Add your website"
                  value={websiteInput}
                  onChange={(e) => {
                    setWebsiteInput(e.target.value)
                    setError(null)
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting}
                  className={`h-13 rounded-r-none border-r-0 pl-4 pr-4 text-base shadow-sm sm:pl-12 ${
                    websiteInput.trim() && !isValidWebsiteInput(websiteInput)
                      ? 'border-destructive focus-visible:ring-destructive'
                      : ''
                  }`}
                />
                <Button 
                  size='lg' 
                  className='h-13 shrink-0 rounded-l-none px-3 text-base sm:px-6'
                  onClick={handleGetStarted}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoaderIcon className='size-4 animate-spin sm:mr-1' />
                      <span className='hidden sm:inline'>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <span className='hidden sm:inline'>Get Started</span>
                      <ArrowRightIcon className='size-4 sm:ml-1' />
                    </>
                  )}
                </Button>
              </div>
              {websiteInput.trim() && !isValidWebsiteInput(websiteInput) ? (
                <p className="text-destructive text-sm">
                  Please enter a valid website (e.g., example.com or https://example.com)
                </p>
              ) : error ? (
                <p className="text-destructive text-sm">{error}</p>
              ) : null}
            </div>
          </div>

          <div className='flex w-full items-center justify-center gap-4 max-sm:flex-col sm:gap-7'>
            <div className='flex flex-1 items-center justify-end gap-3'>
              <div className='flex flex-row items-center justify-center'>
                <AnimatedTooltip
                  items={avatars}
                  className='[&>[data-slot="avatar"]]:border-background -me-3.5 last:-me-0 [&>[data-slot="avatar"]]:border-2 [&>[data-slot="avatar"]]:shadow-md [&>[data-slot="avatar"]]:ring-0'
                />
              </div>
              <div>
                <span className='text-lg font-medium'>12K+</span> <span className='text-muted-foreground'>Users</span>
              </div>
            </div>

            <Separator orientation='vertical' className='data-[orientation=vertical]:h-4 max-sm:hidden' />

            <div className='flex flex-1 items-center gap-3'>
              <Rating readOnly value={4.5} precision={0.5} className='[&_svg]:text-primary' />
              <div>
                <span className='text-lg font-medium'>4.5</span> <span className='text-muted-foreground'>Ratings</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-horizontal className='w-full gap-0'>
        <div className='border-b px-4 sm:px-6 lg:px-8'>
          <div className='mx-auto max-w-7xl border-x'>
            {/* Tabs List */}
            <ScrollArea className='-m-px *:overflow-hidden!'>
              <TabsList className='h-fit w-full -space-x-px rounded-none bg-transparent p-0 group-data-[orientation=horizontal]/tabs:h-fit'>
                {tabs.map(({ icon: Icon, name, value }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className='border-border text-foreground focus-visible:outline-primary/20 data-[state=active]:border-primary/60! data-[state=active]:bg-muted! h-15 flex-1 cursor-pointer rounded-none px-4 py-2.5 text-base focus-visible:ring-0 focus-visible:outline-[3px] focus-visible:-outline-offset-4 data-[state=active]:z-1'
                  >
                    <Icon />
                    {name}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ScrollBar orientation='horizontal' className='z-2' />
            </ScrollArea>
          </div>
        </div>

        <div className='px-4 sm:px-6 lg:px-8'>
          <div className='relative mx-auto h-151 max-w-7xl border-x'>
            {/* Background Dots */}
            <div className='pointer-events-none absolute inset-0 -z-2 bg-[radial-gradient(color-mix(in_oklab,var(--primary)10%,transparent)_2px,transparent_2px)] bg-size-[20px_20px] bg-fixed' />

            {/* Background Gradient Overlay */}
            <div className='bg-background pointer-events-none absolute inset-0 -z-1 flex items-center justify-center mask-[radial-gradient(ellipse_at_center,transparent_20%,black)]' />

            <ScrollArea className='h-full *:data-[slot="scroll-area-viewport"]:h-full [&>[data-slot="scroll-area-viewport"]>div]:h-full'>
              {tabs.map(tab => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className='flex h-full items-center justify-center p-4 sm:p-6 lg:p-8'
                >
                  {tab.content}
                </TabsContent>
              ))}

              <ScrollBar orientation='horizontal' />
            </ScrollArea>
          </div>
        </div>
      </Tabs>
      <div className='border-b' />
    </section>
  )
}

export default HeroSection
